import React, { useEffect, useState, lazy, Suspense, ComponentType } from 'react';
import { Toaster } from 'react-hot-toast';
import { verifyFirebaseSetup } from './utils/verify-firebase';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RequireRole } from './components/auth/RequireRole';
import { UserRole } from './lib/types/auth';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { RoleBasedRedirect } from './components/RoleBasedRedirect';
import { OrbProvider } from './context/OrbContext';
import { ProductEntryProvider } from './context/ProductEntryContext';
import { FloatingChatProvider } from './context/FloatingChatContext';
import { ChatBarProvider } from './context/ChatBarContext';
import { TimerProvider } from './context/TimerContext';
import { TimeTrackingProvider } from './context/TimeTrackingContext';
import { usePageTracking } from './hooks/usePageTracking';
import FinChatsPage from './pages/finChatsPage';
import { initializeAutoSync } from './services/autoSyncService';
import { forceRefreshService } from './services/forceRefreshService';
import { schedulerService } from './services/schedulerService';
import { inventoryStatusSyncService } from './services/inventoryStatusSyncService';
import { DailyProvider } from '@daily-co/daily-react';
import ChatBar from './components/chat/ChatBar';
import { HolidayFloaterListener } from './components/holiday/HolidayFloaterListener';
import GlobalFloatingTimer from './components/task/GlobalFloatingTimer';
import SameDayAppointmentAlert from './components/appointments/SameDayAppointmentAlert';
// Eager import so /intro renders instantly with no Suspense fallback flash.
import LoadingScreen, { LoadingBars } from './components/LoadingScreen';

// Detects the "stale deploy" failure mode: a hashed chunk no longer exists on
// the server, so Hosting's SPA rewrite returns index.html and the browser
// tries to parse HTML as JS ("Unexpected token '<'"), or the dynamic import
// simply fails to fetch. In all these cases the fix is to reload so the fresh
// index.html (with the new chunk hashes) is fetched.
const isStaleChunkError = (error: any): boolean => {
  const msg = String(error?.message || error || '');
  return /Unexpected token '<'|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\d]+ failed/i.test(msg);
};

// Lazy load components with retry mechanism
const retryLazyLoad = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): Promise<{ default: T }> => {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        if (retries === 0) {
          // Out of retries. If this looks like a stale-deploy chunk failure,
          // do a ONE-TIME hard reload to pick up the fresh index.html + new
          // chunk hashes. A short sessionStorage guard prevents reload loops
          // (e.g. if the module genuinely has a runtime error).
          if (isStaleChunkError(error)) {
            try {
              const KEY = 'sc_stale_chunk_reload_ts';
              const last = Number(sessionStorage.getItem(KEY) || '0');
              if (Date.now() - last > 30000) {
                sessionStorage.setItem(KEY, String(Date.now()));
                window.location.reload();
                return; // page is reloading; don't surface the error
              }
            } catch {
              // sessionStorage unavailable — fall through to reject
            }
          }
          reject(error);
          return;
        }
        setTimeout(() => {
          retryLazyLoad(importFn, retries - 1, delay).then(resolve, reject);
        }, delay);
      });
  });
};

// Lazy load pages
const Dashboard = lazy(() => retryLazyLoad(() => import('./pages/Dashboard')));
const PhotoLogPage = lazy(() => retryLazyLoad(() => import('./pages/photo-log/PhotoLogPage')));
const Profile = lazy(() => retryLazyLoad(() => import('./pages/profile/ProfilePage')));
const Settings = lazy(() => retryLazyLoad(() => import('./pages/settings/SettingsPage')));
const GmailConnect = lazy(() => retryLazyLoad(() => import('./components/email/GmailIntegration')));
const StoreEmail = lazy(() => retryLazyLoad(() => import('./pages/store-email/StoreEmailPage')));
const Commissions = lazy(() => retryLazyLoad(() => import('./pages/commissions/CommissionsPage')));
const CommissionsAdmin = lazy(() => retryLazyLoad(() => import('./pages/commissions-admin/CommissionsAdminPage')));

const Tasks = lazy(() => retryLazyLoad(() => import('./components/task/taskCalendar')));
const POS = lazy(() => retryLazyLoad(() => import('./pages/pos/POSPage')));
const POSPIN = lazy(() => retryLazyLoad(() => import('./pages/pos/POSPINPage')));
const JewelryRequest = lazy(() => retryLazyLoad(() => import('./pages/jewelry/JewelryRequestPage')));
const JewelryRequests = lazy(() => retryLazyLoad(() => import('./pages/jewelry-requests/JewelryRequestsPage')));
const RequestLog = lazy(() => retryLazyLoad(() => import('./pages/request-log/RequestLogPage')));
const InteractionLog = lazy(() => retryLazyLoad(() => import('./pages/interactions/InteractionLogPage')));
const LogInteraction = lazy(() => retryLazyLoad(() => import('./pages/interactions/LogInteractionPage')));
const Reports = lazy(() => retryLazyLoad(() => import('./pages/reports/ReportsPage')));
const Marketing = lazy(() => retryLazyLoad(() => import('./pages/marketing/MarketingPage')));
// Import via wrapper to fix React hooks bundling
import ContentCalendarWrapper from './pages/marketing/ContentCalendarWrapper';
const ContentCalendar = ContentCalendarWrapper;
const EmailMarketing = lazy(() => retryLazyLoad(() => import('./pages/marketing/EmailMarketingPage')));
const UserManagement = lazy(() => retryLazyLoad(() => import('./pages/admin/UserManagement')));
const ClairePage = lazy(() => retryLazyLoad(() => import('./pages/claire/ClairePage')));
const MasterClairePage = lazy(() => retryLazyLoad(() => import('./pages/claire/admin/MasterClairePage')));
const ResearchReportPage = lazy(() => retryLazyLoad(() => import('./pages/claire/ResearchReportPage')));
const Template = lazy(() => retryLazyLoad(() => import('./pages/template/template')));
const GroupCalendar = lazy(() => retryLazyLoad(() => import('./pages/group-calendar/GroupCalendarPage')));
const GEMID = lazy(() => retryLazyLoad(() => import('./pages/gem-id/GEMIDPage')));
const CCOTraining = lazy(() => retryLazyLoad(() => import('./pages/cco-training/CCOTrainingPage')));
const TemplateTraining = lazy(() => retryLazyLoad(() => import('./pages/template-training/TemplateTrainingPage')));
const TemplateTrainingAdmin = lazy(() => retryLazyLoad(() => import('./pages/template-training-admin/TemplateTrainingAdminPage')));
const TemplateAdmin = lazy(() => retryLazyLoad(() => import('./pages/admin/TemplateAdmin')));
const MasterInventory = lazy(() => retryLazyLoad(() => import('./pages/master-inventory/MasterInventoryPage')));
const ShopifyOrders = lazy(() => retryLazyLoad(() => import('./pages/shopify-orders/ShopifyOrdersPage')));
const WatchHistory = lazy(() => retryLazyLoad(() => import('./pages/watch-history/WatchHistoryPage')));
const CCORepairs = lazy(() => retryLazyLoad(() => import('./pages/cco-repairs/CCORepairsPage')));
const Repairs = lazy(() => retryLazyLoad(() => import('./pages/repairs/RepairsPage')));
const CustomerDemo = lazy(() => retryLazyLoad(() => import('./pages/customer-demo/CustomerDemoPage')));
const RepairDetail = lazy(() => retryLazyLoad(() => import('./pages/repairs/RepairDetailPage')));
const BuyTrade = lazy(() => retryLazyLoad(() => import('./pages/buy-trade/BuyTradePage')));
const BuyTradeDetail = lazy(() => retryLazyLoad(() => import('./pages/buy-trade/BuyTradeDetailPage')));
const Consignments = lazy(() => retryLazyLoad(() => import('./pages/consignments/ConsignmentsPage')));
const StoreCommissions = lazy(() => retryLazyLoad(() => import('./pages/store-commissions/StoreCommissionsPage')));
const ConsignmentDetail = lazy(() => retryLazyLoad(() => import('./pages/consignments/ConsignmentDetailPage')));
const CCOTransfers = lazy(() => retryLazyLoad(() => import('./pages/cco-transfers/CCOTransfersPage')));
const SuppliesRequest = lazy(() => retryLazyLoad(() => import('./pages/supplies-request/SuppliesRequestPage')));
const Schedule = lazy(() => retryLazyLoad(() => import('./pages/schedule/SchedulePage')));
const Form8300 = lazy(() => retryLazyLoad(() => import('./pages/form-8300/Form8300Page')));
const CaseMap = lazy(() => retryLazyLoad(() => import('./pages/case-map/CaseMapPage')));
const SmsPlatform = lazy(() => retryLazyLoad(() => import('./pages/sms/SmsPlatformPage')));
// Import via wrapper to fix React hooks bundling
import ContentCreationWrapper from './pages/content-creation/ContentCreationPageWrapper';
const ContentCreation = ContentCreationWrapper;
const Accounting = lazy(() => retryLazyLoad(() => import('./pages/accounting/AccountingPage')));
const CompanyRatings = lazy(() => retryLazyLoad(() => import('./pages/company-ratings/CompanyRatingsPage')));
const ThenNowNext = lazy(() => retryLazyLoad(() => import('./pages/then-now-next/ThenNowNextPage')));
const ChallengePage = lazy(() => retryLazyLoad(() => import('./pages/challenge/ChallengePage')));
const FinComms = lazy(() => retryLazyLoad(() => import('./pages/fin-comms/FinCommsPage')));
const Returns = lazy(() => retryLazyLoad(() => import('./pages/returns/ReturnsPage')));
const Brainstorm = lazy(() => retryLazyLoad(() => import('./pages/brainstorm/BrainstormPage')));
const InventoryManagement = lazy(() => retryLazyLoad(() => import('./pages/inventory/InventoryManagementPage')));
const PostingSchedule = lazy(() => retryLazyLoad(() => import('./pages/PostingSchedule')));
const WatchesSFS = lazy(() => retryLazyLoad(() => import('./pages/watches/WatchesSFSPage')));
const LinksManager = lazy(() => retryLazyLoad(() => import('./pages/links-manager/LinksManagerPage')));
const LiveSalesDashboard = lazy(() => retryLazyLoad(() => import('./pages/live-sales-dashboard/LiveSalesDashboardPage')));
const LiveSalesFeed = lazy(() => retryLazyLoad(() => import('./pages/live-sales-feed/LiveSalesFeedPage')));
const LiveSellerView = lazy(() => retryLazyLoad(() => import('./pages/live-seller/LiveSellerView')));
const ModelSubmissions = lazy(() => retryLazyLoad(() => import('./pages/model-submissions/ModelSubmissionsPage')));
const CCOMeetingNotes = lazy(() => retryLazyLoad(() => import('./pages/cco-meeting-notes/CCOMeetingNotesPage')));
const ProductEntry = lazy(() => retryLazyLoad(() => import('./pages/product-entry/ProductEntryPage')));
const AffiliateOrders = lazy(() => retryLazyLoad(() => import('./pages/affiliate-orders/AffiliateOrdersPage')));
const BrandGuidelines = lazy(() => retryLazyLoad(() => import('./pages/brand-guidelines/BrandGuidelinesPage')));
const ChorostManagement = lazy(() => retryLazyLoad(() => import('./pages/chorost-management/ChorostManagementPage')));
const ChorostSettings = lazy(() => retryLazyLoad(() => import('./pages/chorost-management/ChorostSettingsPage')));
const ChorostConnect = lazy(() => retryLazyLoad(() => import('./pages/chorost-connect/ChorostConnectPage')));
const ShopifyBulkEdit = lazy(() => retryLazyLoad(() => import('./pages/shopify-bulk-edit/ShopifyBulkEditPage')));
const EbayBulkEdit = lazy(() => retryLazyLoad(() => import('./pages/ebay-bulk-edit/EbayBulkEditPage')));
const InventoryCheck = lazy(() => retryLazyLoad(() => import('./pages/inventory-check/InventoryCheckPage')));
const Sales = lazy(() => retryLazyLoad(() => import('./pages/sales/SalesPage')));
const ChorostStoreSales = lazy(() => retryLazyLoad(() => import('./pages/sales/ChorostStoreSalesPage')));
const InventoryDatabase = lazy(() => retryLazyLoad(() => import('./pages/inventory-database/InventoryDatabasePage')));
const LiveListings = lazy(() => retryLazyLoad(() => import('./pages/live-listings/LiveListingsPage')));
const CustomJewelryRequest = lazy(() => retryLazyLoad(() => import('./pages/custom-jewelry-request/CustomJewelryRequestPage')));
const PurchaseOrder = lazy(() => retryLazyLoad(() => import('./pages/purchase-order/PurchaseOrderPage')));
const WatchPrices = lazy(() => retryLazyLoad(() => import('./pages/watch-prices/WatchPricesPage')));
const FilesPage = lazy(() => retryLazyLoad(() => import('./pages/files/FilesPage')));
const AppointmentsPage = lazy(() => retryLazyLoad(() => import('./pages/appointments/AppointmentsPage')));
const PublicBookingPage = lazy(() => retryLazyLoad(() => import('./pages/appointments/PublicBookingPage')));
const PricingRequestPage = lazy(() => retryLazyLoad(() => import('./pages/public/PricingRequestPage')));
const PublicSurveyPage = lazy(() => retryLazyLoad(() => import('./pages/survey/PublicSurveyPage')));
const CCOCRM = lazy(() => retryLazyLoad(() => import('./pages/cco-crm/CCOCRMPage')));
const Wishlist = lazy(() => retryLazyLoad(() => import('./pages/wishlist/WishlistPage')));
const Training = lazy(() => retryLazyLoad(() => import('./pages/training/TrainingPage')));
const PublicSalesAgreementSignPage = lazy(() =>
  retryLazyLoad(() => import('./pages/public/PublicSalesAgreementSignPage'))
);
const PublicWindowsPage = lazy(() =>
  retryLazyLoad(() => import('./pages/public/PublicWindowsPage'))
);
const FinRequest = lazy(() => retryLazyLoad(() => import('./pages/fin-request/FinRequestPage')));
const Projects = lazy(() => retryLazyLoad(() => import('./pages/projects/ProjectsPage')));
const WatchWarranties = lazy(() => retryLazyLoad(() => import('./pages/watch-warranties/WatchWarrantiesPage')));
const ChorostHandbook = lazy(() => retryLazyLoad(() => import('./pages/chorost-handbook/ChorostHandbookPage')));
const Playground = lazy(() => retryLazyLoad(() => import('./pages/playground/PlaygroundPage')));
const StoreTill = lazy(() => retryLazyLoad(() => import('./pages/store-till/StoreTillPage')));
const VendorTransactions = lazy(() => retryLazyLoad(() => import('./pages/vendor-transactions/VendorTransactionsPage')));

// Universal Suspense / route-loading fallback. Renders the same black equalizer
// bars on the warm tan surface that the /intro LoadingScreen uses, so every
// "the app is loading something" moment looks the same.
const MinimalLoader = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: '#f0e6d2' }}>
    <LoadingBars size="md" />
  </div>
);

// Standalone public routes that must load fast for external recipients and do
// NOT require the internal app services (see the bootstrap effect below).
const isPublicStandaloneRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /^\/(sign-sales-agreement|book-appointment|survey|public\/windows|price-request)(\/|$)/.test(
    window.location.pathname
  );
};

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track page navigation for analytics
  usePageTracking();

  useEffect(() => {
    // Public, customer-facing standalone pages (signing / booking / survey) don't
    // need any of the internal app services below (inquiry auto-sync, report
    // scheduler, inventory status sync, Shopify metafield checks, or the
    // products verification read). Running them just delays first paint for the
    // customer. Firebase itself is already initialized on import, so these pages
    // can still read Firestore. Skip straight to rendering the route.
    if (isPublicStandaloneRoute()) {
      setLoading(false);
      return;
    }

    const verifySetup = async () => {
      try {
        await verifyFirebaseSetup();
        
        // Initialize auto-sync service for inquiries
        console.log('🚀 Initializing inquiry auto-sync service...');
        initializeAutoSync(1); // Sync every 1 minute
        
        console.log('🔄 Initializing force refresh listener...');
        const unsubForceRefresh = forceRefreshService.initializeListener();
        
        // Initialize monthly report scheduler
        console.log('📅 Starting monthly store goals report scheduler...');
        schedulerService.start();
        
        // Initialize inventory status sync service
        console.log('🔄 Initializing inventory status sync service...');
        inventoryStatusSyncService.initialize().catch(err => {
          console.error('⚠️  Failed to initialize inventory status sync:', err);
          // Non-critical error - app continues to work
        });
        
        // Ensure Shopify metafield definitions exist (runs async, doesn't block app)
        console.log('📝 Ensuring Shopify metafield definitions...');
        import('./services/shopifyService').then(({ getShopifyService }) => {
          const shopifyService = getShopifyService();
          shopifyService.ensureMetafieldDefinitions().catch(err => {
            console.error('⚠️  Failed to ensure metafield definitions:', err);
            // Non-critical error - app continues to work
          });
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Firebase setup error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize Firebase');
        setLoading(false);
      }
    };

    verifySetup();
  }, []);

  if (loading) return <MinimalLoader />;
  if (error) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white p-6">
        <div className="max-w-lg w-full text-center space-y-4">
          <h1 className="text-xl font-semibold text-gray-900">Can’t start the app</h1>
          <p className="text-sm text-gray-700 whitespace-pre-line">{error}</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            If this began right after a deploy or update: open DevTools → Application → Storage →
            clear site data (or unregister the service worker), then hard-reload. Developers:
            confirm all <code className="bg-gray-100 px-1 rounded">VITE_FIREBASE_*</code> variables
            are set in <code className="bg-gray-100 px-1 rounded">.env</code> and rebuild.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  return (
    <OrbProvider>
      <ProductEntryProvider>
        <FloatingChatProvider>
          <ChatBarProvider>
            <TimerProvider>
            <TimeTrackingProvider>
            <DailyProvider>
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/intro" element={<LoadingScreen />} />
            <Route path="/book-appointment" element={<Suspense fallback={<MinimalLoader />}><PublicBookingPage /></Suspense>} />
            <Route path="/price-request/:token" element={<Suspense fallback={<MinimalLoader />}><PricingRequestPage /></Suspense>} />
            <Route path="/public/windows" element={<Suspense fallback={<MinimalLoader />}><PublicWindowsPage /></Suspense>} />
            <Route path="/survey/:surveyId" element={<Suspense fallback={<MinimalLoader />}><PublicSurveyPage /></Suspense>} />
            <Route
              path="/sign-sales-agreement/:token"
              element={
                <Suspense fallback={<MinimalLoader />}>
                  <PublicSalesAgreementSignPage />
                </Suspense>
              }
            />
            <Route path="/brand-guidelines" element={<ProtectedRoute><Suspense fallback={<MinimalLoader />}><BrandGuidelines /></Suspense></ProtectedRoute>} />
            <Route path="/chorost-management/*" element={<ProtectedRoute><Suspense fallback={<MinimalLoader />}><ChorostManagement /></Suspense></ProtectedRoute>} />
            <Route path="/chorost-connect/*" element={<ProtectedRoute><Suspense fallback={<MinimalLoader />}><ChorostConnect /></Suspense></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route
                index
                element={
                  <Suspense fallback={<MinimalLoader />}>
                    <RoleBasedRedirect>
                      <Dashboard />
                    </RoleBasedRedirect>
                  </Suspense>
                }
              />
              <Route path="profile" element={<Suspense fallback={<MinimalLoader />}><Profile /></Suspense>} />
              <Route path="settings" element={<Suspense fallback={<MinimalLoader />}><Settings /></Suspense>} />
              <Route path="connect-gmail" element={<Suspense fallback={<MinimalLoader />}><GmailConnect /></Suspense>} />
              <Route path="store-email" element={<Suspense fallback={<MinimalLoader />}><StoreEmail /></Suspense>} />
              <Route path="commissions" element={<Suspense fallback={<MinimalLoader />}><Commissions /></Suspense>} />
              <Route path="commissions-admin" element={<Suspense fallback={<MinimalLoader />}><CommissionsAdmin /></Suspense>} />

              <Route path="tasks" element={<Suspense fallback={<MinimalLoader />}><Tasks /></Suspense>} />
              <Route path="pos" element={<Suspense fallback={<MinimalLoader />}><POS /></Suspense>} />
              <Route path="pos-pin" element={<Suspense fallback={<MinimalLoader />}><POSPIN /></Suspense>} />
              <Route path="jewelry" element={<Suspense fallback={<MinimalLoader />}><JewelryRequest /></Suspense>} />
              <Route path="jewelry-requests" element={<Suspense fallback={<MinimalLoader />}><JewelryRequests /></Suspense>} />
              <Route path="request-log" element={<Suspense fallback={<MinimalLoader />}><RequestLog /></Suspense>} />
              <Route path="interactions" element={<Suspense fallback={<MinimalLoader />}><InteractionLog /></Suspense>} />
              <Route path="log-interaction" element={<Suspense fallback={<MinimalLoader />}><LogInteraction /></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<MinimalLoader />}><Reports /></Suspense>} />
              <Route path="marketing" element={<Suspense fallback={<MinimalLoader />}><Marketing /></Suspense>} />
              <Route path="content-calendar" element={<Suspense fallback={<MinimalLoader />}><ContentCalendar /></Suspense>} />
              <Route path="email-marketing" element={<Suspense fallback={<MinimalLoader />}><EmailMarketing /></Suspense>} />
              <Route path="photo-log" element={<Suspense fallback={<MinimalLoader />}><PhotoLogPage /></Suspense>} />
              <Route path="group-calendar" element={<Suspense fallback={<MinimalLoader />}><GroupCalendar /></Suspense>} />
              <Route path="template" element={<Suspense fallback={<MinimalLoader />}><Template /></Suspense>} />
              <Route path="users" element={<Suspense fallback={<MinimalLoader />}><UserManagement /></Suspense>} />
              <Route path="claire" element={<Suspense fallback={<MinimalLoader />}><ClairePage /></Suspense>} />
              <Route path="claire/research/:id" element={<Suspense fallback={<MinimalLoader />}><ResearchReportPage /></Suspense>} />
              <Route path="admin/claire" element={<Navigate to="/admin/claire/overview" replace />} />
              <Route path="admin/claire/*" element={<Suspense fallback={<MinimalLoader />}><RequireRole allowedRoles={[UserRole.MasterAdmin]}><MasterClairePage /></RequireRole></Suspense>} />
              <Route path="gem-id" element={<Suspense fallback={<MinimalLoader />}><GEMID /></Suspense>} />
              <Route path="cco-training" element={<Suspense fallback={<MinimalLoader />}><CCOTraining /></Suspense>} />
              <Route path="template-training" element={<Suspense fallback={<MinimalLoader />}><TemplateTraining /></Suspense>} />
              <Route path="template-training-admin" element={<Suspense fallback={<MinimalLoader />}><TemplateTrainingAdmin /></Suspense>} />
              <Route path="admin/templates" element={<Suspense fallback={<MinimalLoader />}><TemplateAdmin /></Suspense>} />
              <Route path="master-inventory" element={<Suspense fallback={<MinimalLoader />}><MasterInventory /></Suspense>} />
              <Route path="shopify-orders" element={<Suspense fallback={<MinimalLoader />}><ShopifyOrders /></Suspense>} />
              <Route path="shopify-bulk-edit" element={<Suspense fallback={<MinimalLoader />}><ShopifyBulkEdit /></Suspense>} />
              <Route path="ebay-bulk-edit" element={<Suspense fallback={<MinimalLoader />}><EbayBulkEdit /></Suspense>} />
              <Route path="inventory-check" element={<Suspense fallback={<MinimalLoader />}><InventoryCheck /></Suspense>} />
              <Route path="sales" element={<Suspense fallback={<MinimalLoader />}><Sales /></Suspense>} />
              <Route path="chorost-store-sales" element={<Suspense fallback={<MinimalLoader />}><ChorostStoreSales /></Suspense>} />
              <Route path="inventory-database" element={<Suspense fallback={<MinimalLoader />}><InventoryDatabase /></Suspense>} />
              <Route path="live-listings" element={<Suspense fallback={<MinimalLoader />}><LiveListings /></Suspense>} />
              <Route path="watch-history" element={<Suspense fallback={<MinimalLoader />}><WatchHistory /></Suspense>} />
              <Route path="cco-repairs" element={<Suspense fallback={<MinimalLoader />}><CCORepairs /></Suspense>} />
              <Route path="repairs" element={<Suspense fallback={<MinimalLoader />}><Repairs /></Suspense>} />
              <Route path="customer-demo" element={<Suspense fallback={<MinimalLoader />}><CustomerDemo /></Suspense>} />
              <Route path="repairs/:repairId" element={<Suspense fallback={<MinimalLoader />}><RepairDetail /></Suspense>} />
              <Route path="buy-trade" element={<Suspense fallback={<MinimalLoader />}><BuyTrade /></Suspense>} />
              <Route path="buy-trade/:txnId" element={<Suspense fallback={<MinimalLoader />}><BuyTradeDetail /></Suspense>} />
              <Route path="consignments" element={<Suspense fallback={<MinimalLoader />}><Consignments /></Suspense>} />
              <Route path="consignments/:consignmentId" element={<Suspense fallback={<MinimalLoader />}><ConsignmentDetail /></Suspense>} />
              <Route path="store-commissions" element={<Suspense fallback={<MinimalLoader />}><StoreCommissions /></Suspense>} />
              <Route path="cco-transfers" element={<Suspense fallback={<MinimalLoader />}><CCOTransfers /></Suspense>} />
              <Route path="supplies-request" element={<Suspense fallback={<MinimalLoader />}><SuppliesRequest /></Suspense>} />
              <Route path="fin-request" element={<Suspense fallback={<MinimalLoader />}><FinRequest /></Suspense>} />
              <Route path="store-till" element={<Suspense fallback={<MinimalLoader />}><StoreTill /></Suspense>} />
              <Route path="vendor-transactions" element={<Suspense fallback={<MinimalLoader />}><VendorTransactions /></Suspense>} />
              <Route path="schedule" element={<Suspense fallback={<MinimalLoader />}><Schedule /></Suspense>} />
              <Route path="form-8300" element={<Suspense fallback={<MinimalLoader />}><Form8300 /></Suspense>} />
              <Route path="case-map" element={<Suspense fallback={<MinimalLoader />}><CaseMap /></Suspense>} />
              <Route path="sms" element={<Suspense fallback={<MinimalLoader />}><SmsPlatform /></Suspense>} />
              <Route path="content-creation" element={<Suspense fallback={<MinimalLoader />}><ContentCreation /></Suspense>} />
              <Route path="accounting" element={<Suspense fallback={<MinimalLoader />}><Accounting /></Suspense>} />
              <Route path="company-ratings" element={<Suspense fallback={<MinimalLoader />}><CompanyRatings /></Suspense>} />
              <Route path="then-now-next" element={<Suspense fallback={<MinimalLoader />}><ThenNowNext /></Suspense>} />
              <Route path="fin-comms" element={<Suspense fallback={<MinimalLoader />}><FinComms /></Suspense>} />
              <Route path="returns" element={<Suspense fallback={<MinimalLoader />}><Returns /></Suspense>} />
              <Route path="brainstorm" element={<Suspense fallback={<MinimalLoader />}><Brainstorm /></Suspense>} />
              <Route path="inventory" element={<Suspense fallback={<MinimalLoader />}><InventoryManagement /></Suspense>} />
              <Route path="posting-schedule" element={<Suspense fallback={<MinimalLoader />}><PostingSchedule /></Suspense>} />
              <Route path="watches-sfs" element={<Suspense fallback={<MinimalLoader />}><WatchesSFS /></Suspense>} />
              <Route path="challenge" element={<Suspense fallback={<MinimalLoader />}><ChallengePage /></Suspense>} />
              <Route path="links-manager" element={<Suspense fallback={<MinimalLoader />}><LinksManager /></Suspense>} />
              <Route path="live-sales-dashboard" element={<Suspense fallback={<MinimalLoader />}><LiveSalesDashboard /></Suspense>} />
              <Route path="live-sales-feed" element={<Suspense fallback={<MinimalLoader />}><LiveSalesFeed /></Suspense>} />
              <Route path="live-seller-view" element={<Suspense fallback={<MinimalLoader />}><LiveSellerView /></Suspense>} />
              <Route path="model-submissions" element={<Suspense fallback={<MinimalLoader />}><ModelSubmissions /></Suspense>} />
              <Route path="cco-meeting-notes" element={<Suspense fallback={<MinimalLoader />}><CCOMeetingNotes /></Suspense>} />
              <Route path="product-entry" element={<Suspense fallback={<MinimalLoader />}><ProductEntry /></Suspense>} />
              <Route path="affiliate-orders" element={<Suspense fallback={<MinimalLoader />}><AffiliateOrders /></Suspense>} />
              <Route path="chats" element={<FinChatsPage />} />
              <Route path="chats/:chatId" element={<FinChatsPage />} />
              <Route path="custom-jewelry-request" element={<Suspense fallback={<MinimalLoader />}><CustomJewelryRequest /></Suspense>} />
              <Route path="purchase-order" element={<Suspense fallback={<MinimalLoader />}><PurchaseOrder /></Suspense>} />
              <Route path="watch-prices" element={<Suspense fallback={<MinimalLoader />}><WatchPrices /></Suspense>} />
              <Route path="files" element={<Suspense fallback={<MinimalLoader />}><FilesPage /></Suspense>} />
              <Route path="appointments" element={<Suspense fallback={<MinimalLoader />}><AppointmentsPage /></Suspense>} />
              <Route path="cco-crm" element={<Suspense fallback={<MinimalLoader />}><CCOCRM /></Suspense>} />
              <Route path="cco-crm/:view" element={<Suspense fallback={<MinimalLoader />}><CCOCRM /></Suspense>} />
              <Route path="wishlist" element={<Suspense fallback={<MinimalLoader />}><Wishlist /></Suspense>} />
              <Route path="training" element={<Suspense fallback={<MinimalLoader />}><Training /></Suspense>} />
              <Route path="projects" element={<Suspense fallback={<MinimalLoader />}><Projects /></Suspense>} />
              <Route path="watch-warranties" element={<Suspense fallback={<MinimalLoader />}><WatchWarranties /></Suspense>} />
              <Route path="chorost-handbook" element={<Suspense fallback={<MinimalLoader />}><ChorostHandbook /></Suspense>} />
              <Route path="playground" element={<Suspense fallback={<MinimalLoader />}><Playground /></Suspense>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </DailyProvider>
          <Toaster position="top-right" />
          <ChatBar />
          <HolidayFloaterListener />
          <GlobalFloatingTimer />
          <SameDayAppointmentAlert />
          </TimeTrackingProvider>
          </TimerProvider>
          </ChatBarProvider>
        </FloatingChatProvider>
      </ProductEntryProvider>
    </OrbProvider>
  );
};

export default App;
